import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';
import { EmailVerificationModal } from '@/components/EmailVerificationModal';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { useGeoComply } from '@/hooks/useGeoComply';
import { useGeoComplyTiming } from '@/hooks/useGeoComplyTiming';
import { useCrossTabSession } from '@/hooks/useCrossTabSession';
import { websocketClient } from '@/lib/websocket-client';
import { initializeNSure } from '@/lib/nsure';
import { processDailyBonusLogin } from '@/hooks/useDailyBonus';
import { toast } from '@/hooks/use-toast';
import { detectInAppBrowser } from '@/lib/in-app-browser';
import { isInAppBrowserOverlayDismissed } from '@/components/ProtectedRoute';
import { trackCompleteRegistration } from '@/lib/pixel';
import { useIsBrowserPromptActive } from '@/stores/browserPromptStore';

interface User {
  uuid: string; // User UUID - primary identifier
  email: string;
  email_verified?: boolean;
  type?: 'customer' | 'admin';
}

interface SocialAuthResult {
  error: any;
  isNewUser?: boolean;
  selfExclusionEnd?: string;
  suspensionData?: any;
  bannedData?: any;
}

interface AuthContextType {
  user: User | null;
  session: { user: User; token: string } | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, referralCode?: string, communicationConsent?: boolean) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any; selfExclusionEnd?: string; suspensionData?: any; bannedData?: any }>;
  signInWithSocial: (token: string, isNewUser?: boolean) => Promise<SocialAuthResult>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>; // Alias for signOut
  /** Re-read session from storage and /auth/me; use after OAuth verification (e.g. vault withdraw) so user stays logged in. */
  reaffirmSession: () => Promise<void>;
  isGeoComplyCompliant: boolean | null; // null = not checked yet, true = compliant, false = non-compliant
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const cleanupAuthState = () => {
  // Remove all auth keys from storage (handles both localStorage and sessionStorage)
  storage.removeItem('auth_token');
  storage.removeItem('user');
  storage.removeItem('session_id');
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<{ user: User; token: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string>('');
  const { checkGeolocation, isInitialized } = useGeoComply();
  const { performCheck: performGeolocationCheckWithTiming, shouldPerformCheck, isInitialized: isTimingInitialized } = useGeoComplyTiming();
  const [pendingGeoComplyCheck, setPendingGeoComplyCheck] = useState(false); // Track if we need to check after SDK initializes
  const [hasCheckedGeoComply, setHasCheckedGeoComply] = useState(false); // Track if we've already checked for this session
  const [isGeoComplyCompliant, setIsGeoComplyCompliant] = useState<boolean | null>(null); // Track if geolocation check passed (null = not checked yet, true = compliant, false = non-compliant)
  const checkInProgressRef = useRef<boolean>(false); // Track if a GeoComply check is currently in progress to prevent duplicates
  const isBrowserPromptActive = useIsBrowserPromptActive(); // A browser permission prompt (location/push) is on screen

  // Check if we're in an in-app browser
  const isInAppBrowser = useMemo(() => detectInAppBrowser().isInAppBrowser, []);

  // Track if the in-app browser overlay has been dismissed (allowing modals/GeoComply to proceed)
  const [inAppOverlayDismissed, setInAppOverlayDismissed] = useState(() => {
    // Initialize from sessionStorage
    return !isInAppBrowser || isInAppBrowserOverlayDismissed();
  });

  // Listen for in-app browser overlay dismissal event
  useEffect(() => {
    if (!isInAppBrowser) return;

    const handleDismissal = () => {
      setInAppOverlayDismissed(true);
    };

    window.addEventListener('inAppBrowserOverlayDismissed', handleDismissal);
    return () => window.removeEventListener('inAppBrowserOverlayDismissed', handleDismissal);
  }, [isInAppBrowser]);

  // Handle session invalidation callback for cross-tab hook
  const handleSessionInvalidated = useCallback((message: string) => {
    setSessionExpiredMessage(message);
    setShowSessionExpiredModal(true);
  }, []);

  // Use cross-tab session management hook
  useCrossTabSession({
    userId: user?.uuid ?? null,
    sessionToken: session?.token ?? null,
    onSessionInvalidated: handleSessionInvalidated,
  });
  
  // Bypass GeoComply only on local development hosts
  const isLocalhost = (): boolean => {
    if (typeof window === 'undefined') return false;

    const hostname = window.location.hostname;
    const port = window.location.port;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      (hostname === 'localhost' && port === '8080') ||
      window.location.href.includes('localhost:8080')
    );
  };
  
  // Use timing hook's isInitialized if available, otherwise fall back to useGeoComply
  const geoComplyInitialized = isTimingInitialized || isInitialized;

  // Track if we're processing a transfer token to prevent race conditions
  const [processingTransferToken, setProcessingTransferToken] = useState(false);
  const transferTokenProcessedRef = useRef(false);

  // Check for transfer token in URL (session transfer from in-app browser)
  // This runs before the regular session check
  useEffect(() => {
    const checkTransferToken = async () => {
      // Prevent duplicate processing
      if (transferTokenProcessedRef.current) return;

      const params = new URLSearchParams(window.location.search);
      const transferToken = params.get('_t');

      if (!transferToken) return;

      // Mark as processed immediately to prevent race conditions
      transferTokenProcessedRef.current = true;

      // Remove token from URL immediately (clean browser history)
      const url = new URL(window.location.href);
      url.searchParams.delete('_t');
      window.history.replaceState({}, '', url.pathname + url.search);

      // If already logged in, skip token verification
      const existingToken = storage.getItem('auth_token');
      if (existingToken) {
        return;
      }

      setProcessingTransferToken(true);

      try {
        const response = await apiClient.post<{
          success: boolean;
          token: string;
          user: User;
        }>('/auth/transfer-token/verify', { token: transferToken });

        if (response.success && response.token && response.user) {
          // Store token and set session (same as normal login)
          apiClient.setAuthToken(response.token);
          storage.setItem('auth_token', response.token);
          storage.setItem('user', JSON.stringify(response.user));

          // Create session ID for this tab
          const sessionId = `${response.user.uuid}_${Date.now()}`;
          storage.setItem('session_id', sessionId);

          setUser(response.user);
          setSession({ user: response.user, token: response.token });

          // Initialize nSure SDK after session transfer
          initializeNSure();

          // Process daily bonus login on session transfer (non-blocking)
          processDailyBonusLogin();

          // Broadcast session restored to other tabs via BroadcastChannel
          try {
            if (typeof BroadcastChannel !== 'undefined') {
              const channel = new BroadcastChannel('auth-session');
              channel.postMessage({
                type: 'session-restored',
                sessionId: sessionId,
                timestamp: Date.now(),
              });
              channel.close();
            }
          } catch {
            // BroadcastChannel not supported
          }

          // Trigger GeoComply check for transferred session
          // If running on localhost, skip the check
          if (isLocalhost()) {
            setHasCheckedGeoComply(true);
            setIsGeoComplyCompliant(true);
          } else {
            // Mark that we need to check GeoComply when SDK is ready
            setPendingGeoComplyCheck(true);
            setHasCheckedGeoComply(false);
          }
        }
      } catch (error) {
        console.error('[AuthContext] Transfer token verification failed:', error);
        // Token invalid/expired - user will need to log in normally
        // Show friendly message to user
        toast({
          title: 'Link expired',
          description: 'Your browser switch link has expired. Please log in to continue.',
          variant: 'default',
          duration: 6000,
        });
      } finally {
        setProcessingTransferToken(false);
      }
    };

    checkTransferToken();
  }, []); // Run once on mount

  useEffect(() => {
    // Check for existing session on mount
    // Skip if we're processing a transfer token
    const checkSession = async () => {
      // Wait for transfer token processing to complete
      if (processingTransferToken) return;
      // OAuth callback in popup: do not run session check or clear storage here.
      // This window will postMessage the token to the opener and close. If we ran
      // checkSession and it failed, we would call cleanupAuthState() and clear
      // localStorage for the whole origin, logging out the opener (e.g. after vault withdraw).
      if (typeof window !== 'undefined' && window.opener && window.location.pathname.includes('/oauth/')) {
        setLoading(false);
        return;
      }
      try {
        const token = storage.getItem('auth_token');
        const storedUser = storage.getItem('user');

        if (token && storedUser) {
          apiClient.setAuthToken(token);
          
          // Verify token is still valid using combined session endpoint
          try {
            // Single call to validate session and get user info
            const sessionResponse = await apiClient.get('/auth/session').catch(() => null);
            if (!sessionResponse || !sessionResponse.valid) {
              // Session was invalidated, clear state
              cleanupAuthState();
              apiClient.setAuthToken(null);
              setLoading(false);
              return;
            }
            
            const verified = { user: sessionResponse.user };
            if (verified.user) {
              storage.setItem('user', JSON.stringify(verified.user));
              setUser(verified.user);
              setSession({ user: verified.user, token });
              
              // Initialize nSure SDK after session is restored
              // This allows the SDK to gather device and session information for chargeback protection
              initializeNSure();

              // Process daily bonus login on session restore - earns today's reward (non-blocking)
              processDailyBonusLogin();

              // When a tab restores a session, create/update a unique session_id for this tab
              // This allows us to detect when another tab opens with the same session
              const existingSessionId = storage.getItem('session_id');
              const newSessionId = `${verified.user.uuid}_${Date.now()}`;
              
              // If no session_id exists, create one
              // If one exists but this is a new tab (detected by checking if we're the first tab),
              // we'll update it to trigger storage events in other tabs
              if (!existingSessionId) {
                storage.setItem('session_id', newSessionId);
              } else {
                // Check if this is a new tab by seeing if we can detect other tabs
                // If we're restoring and there's already a session_id, this might be a new tab
                // Update session_id to trigger storage event in other tabs
                storage.setItem('session_id', newSessionId);
              }
              
              // Broadcast that this tab has restored a session.
              // This will trigger other tabs to check if their session is still valid
              try {
                if (typeof BroadcastChannel !== 'undefined') {
                  const channel = new BroadcastChannel('auth-session');
                  channel.postMessage({
                    type: 'session-restored',
                    sessionId: newSessionId,
                    timestamp: Date.now(),
                  });
                  channel.close();
                }
              } catch {
                // BroadcastChannel not supported
              }
              
              // If running on localhost, mark as compliant immediately
              if (isLocalhost()) {
                setHasCheckedGeoComply(true);
                setIsGeoComplyCompliant(true);
                setPendingGeoComplyCheck(false);
              } else if (isInAppBrowser && !inAppOverlayDismissed) {
                // In-app browser and overlay still showing - wait for user to dismiss
                setPendingGeoComplyCheck(true);
                setHasCheckedGeoComply(false);
              } else if (geoComplyInitialized && shouldPerformCheck()) {
              // Check if 20 minutes have passed since the last check
              // Only perform check if needed (20 minutes since last check)
                // SDK is ready and 20 minutes have passed, perform check
                try {
                  const isCompliant = await performGeolocationCheckWithTiming('access the platform');
                  
                  // Mark as checked
                  setHasCheckedGeoComply(true);
                  
                  // If location check fails or is non-compliant, log user out
                  if (!isCompliant) {
                    setIsGeoComplyCompliant(false);
                    cleanupAuthState();
                    apiClient.setAuthToken(null);
                    setUser(null);
                    setSession(null);
                    setLoading(false);
                    return;
                  }
                  setIsGeoComplyCompliant(true);
                } catch {
                  // On error, log out for security
                  cleanupAuthState();
                  apiClient.setAuthToken(null);
                  setUser(null);
                  setSession(null);
                  setLoading(false);
                  return;
                }
              } else if (geoComplyInitialized && !shouldPerformCheck()) {
                // SDK is ready but less than 20 minutes have passed since last check
                // Mark as checked (we're using the existing check from before refresh)
                setHasCheckedGeoComply(true);
                setPendingGeoComplyCheck(false);
                setIsGeoComplyCompliant(true); // Session was valid, so geolocation was compliant
              } else {
                // SDK not ready - mark that we need to check when SDK initializes
                setPendingGeoComplyCheck(true);
                setHasCheckedGeoComply(false);
              }
              
              // Email verification modal will be shown after geolocation check passes
              // (handled by useEffect that watches hasCheckedGeoComply and isGeoComplyCompliant)
              setLoading(false);
              return;
            }
          } catch (error) {
            // Token invalid, clear it
            cleanupAuthState();
          }
        }
      } catch {
        cleanupAuthState();
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [processingTransferToken]);

  /**
   * Perform GeoComply check when user logs in/registers and SDK is initialized
   * 
   * This effect triggers the location check when:
   * 1. User exists (user) - can be logged in (with session) or registered (without session yet)
   * 2. SDK is initialized (geoComplyInitialized)
   * 3. We have a pending check (pendingGeoComplyCheck && !hasCheckedGeoComply)
   * 
   * IMPORTANT: This effect runs whenever any of these dependencies change:
   * - user: When user logs in/out
   * - session: When session is created/destroyed
   * - geoComplyInitialized: When SDK becomes ready
   * - pendingGeoComplyCheck: When a check is requested
   * - hasCheckedGeoComply: When check status changes
   */
  useEffect(() => {
    const performCheck = async () => {
      if (!user) {
        // Reset check flags when user logs out
        setPendingGeoComplyCheck(false);
        setHasCheckedGeoComply(false);
        return;
      }

      // Early return if no pending check or already checked
      if (!pendingGeoComplyCheck || hasCheckedGeoComply) {
        return;
      }

      // If running on localhost, mark as compliant immediately
      if (isLocalhost()) {
        setHasCheckedGeoComply(true);
        setIsGeoComplyCompliant(true);
        setPendingGeoComplyCheck(false);
        return;
      }

      // If in an in-app browser and overlay is still showing, wait for dismissal
      if (isInAppBrowser && !inAppOverlayDismissed) {
        // Don't mark as checked - keep pendingGeoComplyCheck true so it runs when dismissed
        return;
      }

      // Wait for SDK to initialize
      if (!geoComplyInitialized) {
        return; // Wait for SDK to initialize - this effect will run again when SDK is ready
      }

      // Both conditions met: user exists and SDK is initialized
      // Determine action: if we have a session and pendingGeoComplyCheck was set by signIn (not session restore),
      // use 'login'. Otherwise use appropriate action.
      const action = session ? 'login' : 'register';

      // Don't set hasCheckedGeoComply to true yet - wait until check completes
      // This ensures modals don't show until geolocation check is actually complete
      // We'll set it after the check completes (success or failure)

      try {
        const isCompliant = await performGeolocationCheckWithTiming(action);
        
        // Mark as checked AFTER the check completes
        setHasCheckedGeoComply(true);
        
        // If location check fails or is non-compliant, log user out or clear state
        if (!isCompliant) {
          setIsGeoComplyCompliant(false);
          if (session) {
            // Clean up auth state for logged-in user
            cleanupAuthState();
            apiClient.setAuthToken(null);
            setUser(null);
            setSession(null);
          } else {
            // Clear user state for registration
            setUser(null);
          }
          setPendingGeoComplyCheck(false);
          setHasCheckedGeoComply(false);
          // Note: performGeolocationCheck already shows a toast
        } else {
          setIsGeoComplyCompliant(true);
        }
        
        // Mark check as complete
        checkInProgressRef.current = false;
      } catch (error: any) {
        // Check if this is an SDK error that was already handled
        // SDK errors might return isCompliant: true from performGeolocationCheck
        const isSdkError = error?.message?.includes('code 614') || 
                          error?.message?.includes('unexpected error') ||
                          error?.message?.includes('Geolocation check failed with code');
        
        if (isSdkError) {
          // If we already have a compliant result, don't log out
          if (isGeoComplyCompliant === true || hasCheckedGeoComply) {
            // Still mark as complete
            checkInProgressRef.current = false;
            return; // Don't log out if check already succeeded
          }
        }
        
        // On error, log out/clear state for security (fail securely)
        if (session) {
          cleanupAuthState();
          apiClient.setAuthToken(null);
          setUser(null);
          setSession(null);
        } else {
          storage.removeItem('user'); // Clear from localStorage
          setUser(null);
        }
        setPendingGeoComplyCheck(false);
        setHasCheckedGeoComply(false);
        setIsGeoComplyCompliant(false);
        
        // Mark check as complete (even on error)
        checkInProgressRef.current = false;
      }
    };

    performCheck();
  }, [user, session, geoComplyInitialized, pendingGeoComplyCheck, hasCheckedGeoComply, performGeolocationCheckWithTiming, isInAppBrowser, inAppOverlayDismissed]);

  /**
   * Show email verification modal when user is not email verified
   * 
   * Email verification is critical and should not be blocked by GeoComply.
   * GeoComply checks are handled separately and will block other actions
   * (gameplay, purchases, etc.) but email verification must always be accessible.
   *
   * The only exception is while a browser permission prompt is on screen: the modal
   * would cover it and swallow the taps meant for it. That window is short lived and
   * always released, so the modal reappears as soon as the prompt is dismissed.
   */
  useEffect(() => {
    if (!user) {
      setShowEmailVerificationModal(false);
      return;
    }

    // User should have a session now (both registration and login create sessions)
    // But we still check if session exists for safety
    if (!session) {
      setShowEmailVerificationModal(false);
      return;
    }

    // Show email verification modal if:
    // 1. Email is not verified
    // 2. NOT on /verify-email page (that page handles verification via link click —
    //    showing the modal on top of it creates a race condition where the modal
    //    steals focus while the page is auto-verifying in the background)
    // 
    // Note: GeoComply status is intentionally NOT checked here. Email verification
    // is critical and should always be accessible, even if GeoComply fails to initialize.
    const isOnVerifyEmailPage = window.location.pathname === '/verify-email';
    if (!user.email_verified && !isOnVerifyEmailPage && !isBrowserPromptActive) {
      setShowEmailVerificationModal(true);
    } else {
      setShowEmailVerificationModal(false);
    }
  }, [user, session, isBrowserPromptActive]);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string, referralCode?: string, communicationConsent?: boolean) => {
    try {
      // Clean up existing state
      cleanupAuthState();

      // Sanitize referral code - only allow alphanumeric, convert to uppercase
      let sanitizedReferralCode: string | undefined = undefined;
      if (referralCode) {
        const trimmed = referralCode.trim();
        // Remove any non-alphanumeric characters and convert to uppercase
        const sanitized = trimmed.replace(/[^A-Z0-9]/gi, '').toUpperCase();
        // Only use if valid format and not empty
        if (sanitized.length > 0 && sanitized.length <= 20) {
          sanitizedReferralCode = sanitized;
        }
      }

      const result = await apiClient.post<{ success: boolean; message: string; user: User; token?: string }>('/auth/register', {
        email,
        password,
        firstName,
        lastName,
        referralCode: sanitizedReferralCode,
        communicationConsent: communicationConsent ?? true, // Default to true if not provided
      });

      // Registration now returns a token and creates a session (same as login)
      // User still needs to verify email, but they're authenticated
      if (result.user) {
        const token = result.token || storage.getItem('auth_token');
        
        if (token) {
          apiClient.setAuthToken(token);
          storage.setItem('auth_token', token);
          // Create unique session ID for this tab
          storage.setItem('session_id', `${result.user.uuid}_${Date.now()}`);
        }
        
        // Store user in localStorage
        storage.setItem('user', JSON.stringify(result.user));
        setUser(result.user);
        
        // Create session (same as login)
        if (token) {
          setSession({ user: result.user, token });
        }
        
        // Track registration conversion in Meta Pixel
        trackCompleteRegistration(email);

        // Initialize nSure SDK after successful registration
        initializeNSure();

        // Process daily bonus login - earns today's reward for new users (non-blocking)
        processDailyBonusLogin();

        // Mark that we need to check GeoComply (useEffect will handle it)
        setPendingGeoComplyCheck(true);
        setHasCheckedGeoComply(false);
        
        // If running on localhost, skip GeoComply check
        if (isLocalhost()) {
          setIsGeoComplyCompliant(true);
          setHasCheckedGeoComply(true);
          setPendingGeoComplyCheck(false);
        }
        
        // Email verification modal will be shown after geolocation check passes
        // (handled by useEffect that watches hasCheckedGeoComply and isGeoComplyCompliant)
      }
      
      return { error: null };
    } catch (error: any) {
      return { 
        error: { 
          message: error.message || 'Registration failed' 
        } 
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Clean up existing state
      cleanupAuthState();
      
      // Reset GeoComply check flags for new login session
      // This ensures geolocation check runs on every login, not just the first one
      setPendingGeoComplyCheck(false);
      setHasCheckedGeoComply(false);

      // Note: Account status checks (self-exclusion, suspension, lock, ban) 
      // should be handled by the backend during login
      const result = await apiClient.post<{ success: boolean; user: User; token?: string }>('/auth/login', {
        email,
        password,
      });

      // Backend sets token in HTTP-only cookie, but we need it in Authorization header
      // Extract token from cookie if available, otherwise we'll use the cookie directly
      // Note: We can't read HTTP-only cookies from JS, so we rely on the cookie being sent automatically
      // For API client, we'll need to ensure cookies are sent with requests (credentials: 'include')
      
      // Try to get token from response if provided, otherwise use stored token
      const token = result.token || storage.getItem('auth_token');
      
      if (token) {
        apiClient.setAuthToken(token);
        storage.setItem('auth_token', token);
        // Store session identifier to detect new logins in other tabs
        storage.setItem('session_id', `${result.user.uuid}_${Date.now()}`);
      }
      
      storage.setItem('user', JSON.stringify(result.user));
      
      setUser(result.user);
      setSession({ user: result.user, token: token || '' });

      // Initialize nSure SDK after successful login
      initializeNSure();

      // Process daily bonus login - earns today's reward (non-blocking)
      processDailyBonusLogin();

      // Mark that we need to check GeoComply (useEffect will handle the actual check)
      setPendingGeoComplyCheck(true);
      setHasCheckedGeoComply(false);
      setIsGeoComplyCompliant(null); // Reset compliance status for new login

      // DO NOT show email verification modal yet - wait for geolocation check to complete
      // The modal will be shown after geolocation check passes (see useEffect below)
      
      return { error: null };
    } catch (error: any) {
      // api-client puts parsed JSON body on error.response (not error.response.data)
      const responseData = error.response?.data ?? error.response;
      const errorMessage =
        (typeof responseData?.message === 'string' ? responseData.message : null) ||
        error.message ||
        'Login failed';

      if (errorMessage.includes('self-excluded') || responseData?.message === 'Account is self-excluded') {
        const exclusionEndDate =
          responseData?.exclusionEndDate ||
          (typeof errorMessage === 'string' && errorMessage.includes('until ')
            ? errorMessage.replace(/.*until\s+/i, '').trim()
            : undefined);
        return {
          error: { message: 'Account is self-excluded' },
          selfExclusionEnd: exclusionEndDate,
        };
      }
      if (errorMessage.includes('suspended')) {
        return { 
          error: { message: 'Account is suspended' },
          suspensionData: responseData?.suspensionData || {
            reason: null,
            endDate: null,
          }
        };
      }
      if (errorMessage.includes('banned')) {
        return { 
          error: { message: 'Account is banned' },
          bannedData: responseData?.bannedData || {
            reason: null,
            bannedAt: null,
          }
        };
      }

      return {
        error: {
          message: errorMessage
        }
      };
    }
  };

  /**
   * Sign in with social auth (OAuth)
   * Called after successful OAuth flow with token from backend
   */
  const signInWithSocial = async (token: string, isNewUser: boolean = false): Promise<SocialAuthResult> => {
    try {
      // Clean up existing state
      cleanupAuthState();

      // Reset GeoComply check flags for new login session
      setPendingGeoComplyCheck(false);
      setHasCheckedGeoComply(false);

      // Set the token
      apiClient.setAuthToken(token);
      storage.setItem('auth_token', token);

      // Fetch user info
      const verified = await apiClient.get('/auth/me');

      if (!verified.user) {
        throw new Error('Failed to get user info');
      }

      // Store session identifier
      storage.setItem('session_id', `${verified.user.uuid}_${Date.now()}`);
      storage.setItem('user', JSON.stringify(verified.user));

      // Policy check is now handled by PolicyAcceptanceContext

      setUser(verified.user);
      setSession({ user: verified.user, token });

      // Track registration conversion in Meta Pixel for new social auth users
      if (isNewUser) {
        trackCompleteRegistration(verified.user.email);
      }

      // Initialize nSure SDK after successful social login
      initializeNSure();

      // Process daily bonus login - earns today's reward (non-blocking)
      processDailyBonusLogin();

      // Mark that we need to check GeoComply
      setPendingGeoComplyCheck(true);
      setHasCheckedGeoComply(false);
      setIsGeoComplyCompliant(null);

      // If running on localhost, skip GeoComply check
      if (isLocalhost()) {
        setIsGeoComplyCompliant(true);
        setHasCheckedGeoComply(true);
        setPendingGeoComplyCheck(false);
      }

      return { error: null, isNewUser };
    } catch (error: any) {
      cleanupAuthState();
      apiClient.setAuthToken(null);
      return {
        error: {
          message: error.message || 'Social authentication failed'
        }
      };
    }
  };

  const signOut = async () => {
    try {
      // Clean up auth state
      cleanupAuthState();
      apiClient.setAuthToken(null);
      
      setUser(null);
      setSession(null);
      setIsGeoComplyCompliant(null); // Reset compliance status on logout
      // Policy acceptance state is reset by PolicyAcceptanceContext when user becomes null
      
      // Force page reload for clean state
      window.location.href = '/';
    } catch (error) {
      // Handle error silently and redirect anyway
      cleanupAuthState();
      window.location.href = '/';
    }
  };

  // Alias for logout function
  const logout = signOut;

  /**
   * Re-read session from storage and /auth/me. Use after OAuth verification (e.g. vault withdraw
   * with Google) so the opener's session stays in sync and the user is not logged out on reload.
   * Does not clear auth state on failure (caller is already logged in).
   */
  const reaffirmSession = useCallback(async () => {
    const token = storage.getItem('auth_token');
    if (!token) return;
    try {
      apiClient.setAuthToken(token);
      const sessionResponse = await apiClient.get('/auth/session').catch(() => null);
      if (sessionResponse?.valid && sessionResponse.user) {
        storage.setItem('user', JSON.stringify(sessionResponse.user));
        if (!storage.getItem('session_id')) {
          storage.setItem('session_id', `${sessionResponse.user.uuid}_${Date.now()}`);
        }
        setUser(sessionResponse.user);
        setSession({ user: sessionResponse.user, token });
      }
    } catch (error) {
      console.warn('[AuthContext] reaffirmSession failed (session unchanged):', error);
    }
  }, []);

  const handleEmailVerified = async () => {
    // Refresh user data to get updated verification status
    try {
      const verified = await apiClient.get('/auth/me');
      if (verified.user) {
        const currentToken = session?.token || storage.getItem('auth_token') || '';
        storage.setItem('user', JSON.stringify(verified.user));
        setUser(verified.user);
        setSession({ user: verified.user, token: currentToken });
        setShowEmailVerificationModal(false);
      }
    } catch {
      // Failed to refresh user data
    }
  };

  /**
   * Handle session expired modal close - logout user
   */
  const handleSessionExpiredClose = async () => {
    setShowSessionExpiredModal(false);
    await signOut();
  };

  /**
   * Set up WebSocket listener for session invalidation
   * Note: Multiple concurrent login sessions are now allowed (30-day persistence across devices).
   * This listener is only triggered by admin-initiated session invalidation, not by multi-device login.
   * Single-game-at-a-time is enforced at the game_session level when launching games.
   */
  useEffect(() => {
    if (!user) return;

    // Subscribe to session invalidation events via WebSocket (admin-initiated only)
    const unsubscribe = websocketClient.subscribe('session:invalidated', (data: { message: string }) => {
      handleSessionInvalidated(data.message || 'Your session has ended.');
    });

    return () => {
      unsubscribe();
    };
  }, [user, handleSessionInvalidated]);


  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithSocial,
    signOut,
    logout, // Alias for signOut
    reaffirmSession,
    isGeoComplyCompliant, // Export compliance status for components that need to wait
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showEmailVerificationModal && user && (
        <EmailVerificationModal
          isOpen={showEmailVerificationModal}
          email={user.email}
          onVerified={handleEmailVerified}
        />
      )}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        message={sessionExpiredMessage}
        onClose={handleSessionExpiredClose}
      />
    </AuthContext.Provider>
  );
};
